import * as React from "react";
import identity from "lodash/identity";
import pickBy from "lodash/pickBy";
import { Context } from "@grandvisionhq/carts-v2";
import { toast } from "@grandvisionhq/common";
import { Hooks } from "@grandvisionhq/payments";
import { useIntl } from "@grandvisionhq/state";
import { convertCentsToDecimalString } from "@grandvisionhq/utils";
import { mapCartExpressPaymentMethods, mapCartRegularPaymentMethods } from "../../../../../../mappers/modules/ExpressCheckout/hooks/apple-pay/mappers/apple-pay-payment-methods.js";
import { useMapShippingContact } from "../../../../../../utility-hooks/modules/ExpressCheckout/hooks/apple-pay/utility-hooks/use-map-shipping-contact.js";
import { useRequiredContactFields } from "../../../../../../utility-hooks/modules/ExpressCheckout/hooks/apple-pay/utility-hooks/use-required-contact-fields.js";
import { CheckoutErrorsMapping } from "../../../../../../../../../../mappers/mappers/errors-mapping.js";
import { useCheckoutSettings } from "../../../../../../../../../../context/CheckoutSettings/context/CheckoutSettings/context.js";
const useCreatePaymentRequest = (isExpressPayment = true) => {
  const { country, currency } = useIntl();
  const [getCartExpressPaymentMethods, paymentMethodsResult] = Hooks.useGetCartExpressPaymentMethods();
  const [getPaymentMethods, queryResult] = Hooks.useGetCartPaymentMethods();
  const { cart } = Context.useCart();
  const requiredContactFields = useRequiredContactFields();
  const { shippingContact, shippingContactEditingMode, shippingType } = useMapShippingContact(cart);
  const { payments } = useCheckoutSettings();
  const [error, setError] = React.useState(void 0);
  const expressPaymentMethods = React.useMemo(
    () => paymentMethodsResult?.data ?? [],
    [paymentMethodsResult?.data]
  );
  const regularPaymentMethods = React.useMemo(() => queryResult.data ?? [], [queryResult.data]);
  const handlePaymentMethodsBrandSettings = React.useCallback(
    (cartId) => {
      if (cartId) {
        isExpressPayment ? getCartExpressPaymentMethods(cartId) : getPaymentMethods(cartId);
      }
    },
    [getCartExpressPaymentMethods, getPaymentMethods, isExpressPayment]
  );
  React.useEffect(() => {
    if (cart?.id) {
      handlePaymentMethodsBrandSettings(cart.id);
    }
  }, [cart?.id]);
  const createPaymentRequest = React.useCallback(() => {
    if (!cart?.id) {
      console.error("No cart available.");
      setError(CheckoutErrorsMapping.CartIdNotFound);
      toast.failed(CheckoutErrorsMapping.CartIdNotFound);
      return;
    }
    if (!paymentMethodsResult?.data) {
      console.error("No payment methods available.");
      setError(CheckoutErrorsMapping.PaymentMethodNotFound);
      toast.failed(CheckoutErrorsMapping.PaymentMethodNotFound);
      return;
    }
    if (!payments?.applePay?.merchantName) {
      console.error("No merchant name available.");
    }
    const totalPrice = cart?.prices?.totalPrice;
    const total = {
      label: payments?.applePay?.merchantName || "GrandVision N.V.",
      amount: convertCentsToDecimalString(totalPrice),
      type: "final"
    };
    const merchantCapabilities = new Array("supports3DS");
    const paymentRequest = {
      requiredShippingContactFields: requiredContactFields,
      requiredBillingContactFields: requiredContactFields,
      countryCode: country,
      currencyCode: currency,
      merchantCapabilities,
      total,
      supportedNetworks: isExpressPayment ? mapCartExpressPaymentMethods(expressPaymentMethods) : mapCartRegularPaymentMethods(regularPaymentMethods),
      shippingContact,
      shippingType,
      shippingContactEditingMode
    };
    return pickBy(paymentRequest, identity);
  }, [
    cart?.id,
    cart?.prices?.totalPrice,
    paymentMethodsResult?.data,
    payments?.applePay?.merchantName,
    requiredContactFields,
    country,
    currency,
    isExpressPayment,
    expressPaymentMethods,
    regularPaymentMethods,
    shippingContact,
    shippingType,
    shippingContactEditingMode
  ]);
  return [
    createPaymentRequest,
    {
      error,
      loading: paymentMethodsResult.loading || false
    }
  ];
};
export {
  useCreatePaymentRequest
};
//# sourceMappingURL=use-create-payment-request.js.map
